import { Text, View } from "@react-pdf/renderer";
import { useTheme } from "@mui/material";
import { hexToRGBA, thousandSeparator } from "@utils";
import {
  PortfolioCompositionRowDTO,
  PortfolioCompositionSectionDTO,
  PortfolioCompositionSetupDTO,
  ProductRecommendationDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { isNil } from "lodash";
import { PDFStyleConfigs } from "../../export-pdf/configs";
import { PDFTableHeaderWithoutLogo } from "../../export-pdf/components/tables/table-header-without-logo";
import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";
import { PDFTable } from "../../export-pdf/components/tables/table";
import { PDFTableBody } from "../../export-pdf/components/tables/table-body";
import { PDFTableRow } from "../../export-pdf/components/tables/table-row";
import { PDFTitle } from "../../export-pdf/components/title";
import { displayCurrencyNumber } from "../../export-pdf/utils";
import { SelectedProductDTO } from "@models/platform-analysis/entities/steps/feature";
import { shouldHideSubProductName } from "@pages/platform-analysis-v2/_id/utils";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";

export const PDFPortfolioComposition = ({
  startSetupIndex = 0,
  endSetupIndex = 0,
  belowMinimumInvestmentSize,
  isAppending,
  isFollowingTable,
  shouldHideSubTotal,
  numOfHiddenProducts,
  setups,
  totalSetup,
  sections,
  productRecommendations,
  isReview,
  isAppendix,
}: {
  rows?: PortfolioCompositionRowDTO[];
  startSetupIndex?: number;
  endSetupIndex?: number;
  belowMinimumInvestmentSize?: boolean;
  isAppending?: boolean;
  isFollowingTable?: boolean;
  shouldHideSubTotal?: boolean;
  numOfHiddenProducts?: number;
  setups?: PortfolioCompositionSetupDTO[];
  totalSetup: number;
  sections?: PortfolioCompositionSectionDTO[];
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  isReview?: boolean;
  isAppendix?: boolean;
}) => {
  const { typographyStyle } = useAnalysisPDFStyles();
  const { palette } = useTheme();

  const renderTableTitle = () => {
    if (setups?.length === 0) return null;

    return (
      <View
        style={{
          height: PDFStyleConfigs.CompositionTableTitleHeight,
          marginTop: -1,
        }}
      >
        <Text
          style={{
            ...typographyStyle.body1,
            color: palette.common.black,
          }}
        >
          {setups?.length === 1
            ? `(Product ${startSetupIndex} of ${totalSetup})`
            : `(Products ${startSetupIndex}-${Math.min(
                endSetupIndex,
                totalSetup,
              )} of ${totalSetup})`}
        </Text>
      </View>
    );
  };

  const renderTitle = () => {
    if (isAppendix){
      return (
        <>
          <View
            style={{
              height: PDFStyleConfigs.CompositionTableTitleHeight,
            }}
          >
            <Text
              style={{
                ...typographyStyle.subtitle,
                color: palette.common.black,
              }}
            >
              {"Portfolio composition by platform of platforms that were not selected"}
            </Text>
          </View>
          {renderTableTitle()}
        </>
      );
    }

    if (!isAppending) {
      return (
        <>
          <PDFTitle
            title={"Fee analysis (continued)"}
            subTitle={"Portfolio composition by platform"}
          />
          {renderTableTitle()}
        </>
      );
    }

    return isAppending || isFollowingTable ? (
      <>
        <View
          style={{
            height: PDFStyleConfigs.CompositionTableTitleHeight,
          }}
        >
          <Text
            style={{
              ...typographyStyle.subtitle,
              color: palette.common.black,
            }}
          >
            {"Portfolio composition by platform"}
          </Text>
        </View>
        {renderTableTitle()}
      </>
    ) : (
      <>
        <PDFTitle
          title={"Fee analysis (continued)"}
          subTitle={"Portfolio composition by platform"}
        />
        {renderTableTitle()}
      </>
    );
  };

  const renderNumberValue = (value: number) => {
    if (value === 0) return value;

    if (value >= PDFStyleConfigs.MaxAccountBalance) {
      const roundedValue = Math.ceil(value);

      return displayCurrencyNumber(roundedValue);
    }

    const fixedValue = Number(
      value.toFixed(PDFStyleConfigs.CompositionFixedDecimalPlaces),
    );
    const showDecimalPlaces = fixedValue % 1 !== 0;

    return thousandSeparator(
      value.toFixed(
        showDecimalPlaces ? PDFStyleConfigs.CompositionFixedDecimalPlaces : 0,
      ),
    );
  };

  const selectedProducts = setups?.map((item) => {
    const {
      isProductMain,
      productName,
      name,
      logo,
      order,
      productId,
      setupType,
    } = item;

    const product: SelectedProductDTO = {
      id: productId ?? "",
      isProductMain: Boolean(isProductMain),
      order: order,
      productLogo: {
        key: logo,
        url: logo,
      },
      productName: productName,
      subProductName: shouldHideSubProductName(
        setupType as AnalysisFeePortfolioSetupType,
        productName,
      )
        ? ""
        : name,
      totalScore: 0,
    };

    return product;
  });

  return (
    <View style={{ position: "relative" }}>
      {renderTitle()}

      {setups?.length === 0 && (
        <View style={{ gap: "5px", marginTop: 15, marginBottom: 15 }}>
          <Text
            style={{
              ...typographyStyle.body1,
              fontWeight: "bold",
              color: "#b1b6bf",
            }}
          >
            No data
          </Text>
          <Text style={{ ...typographyStyle.body1, color: "#b1b6bf" }}>
            No products match the criteria in your filter
          </Text>
        </View>
      )}

      {setups && setups.length > 0 && (
        <>
          <PDFTable
            tableWidth={
              PDFStyleConfigs.TableFirstCellWidth +
              setups.length * PDFStyleConfigs.CompositionFollowingCellWidth
            }
          >
            <PDFTableHeaderWithoutLogo
              showSubProductName
              forceShowBorderBottom
              selectedProducts={selectedProducts}
              title={"Portfolio composition by platform"}
              staticFirstCellWidth={PDFStyleConfigs.TableFirstCellWidth}
              staticFollowingCellWidth={
                PDFStyleConfigs.CompositionFollowingCellWidth
              }
              titleFontSize={PDFStyleConfigs.CompositionRowNameFontSize}
              imageSize={0}
              productRecommendations={productRecommendations}
              isReview={isReview}
            />

            <PDFTableBody>
              {sections?.map((section, sectionIndex) => {
                const { rows } = section;

                return rows.map((row, rowIndex) => {
                  const { cells, name, isSingleRow, isSubTotal } = row;

                  return (
                    <PDFTableRow
                      key={`${sectionIndex}-${rowIndex}`}
                      color={isSubTotal || isSingleRow ? "gray" : "white"}
                    >
                      <View
                        style={{
                          width: PDFStyleConfigs.TableFirstCellWidth,
                          alignItems: "flex-start",
                          paddingLeft: 5,
                          paddingRight: 5,
                          paddingTop: 4,
                          paddingBottom: 4,
                          flexDirection: "column",
                          borderColor: "#7C8493",
                          borderRightWidth: 1,
                        }}
                      >
                        <View
                          style={{
                            paddingLeft:
                              !isSubTotal && !shouldHideSubTotal ? 5 : 0,
                          }}
                        >
                          <Text
                            style={{
                              ...typographyStyle.body2,
                              color: palette.text.primary,
                              fontSize:
                                PDFStyleConfigs.CompositionRowNameFontSize,
                            }}
                          >
                            {name}
                          </Text>
                        </View>
                      </View>

                      {cells.map((cell, cellIndex) => {
                        const {
                          value,
                          valueInPercent,
                          belowMinimumInvestmentSize,
                        } = cell || {};
                        const numberValue = Number(value);
                        const numberValueInPercent = Number(valueInPercent);
                        const invalidValues =
                          isNaN(numberValue) || isNaN(numberValueInPercent);

                        if (!value || !valueInPercent) return <></>;

                        return (
                          <View
                            key={`${sectionIndex}-${rowIndex}-${cellIndex}`}
                            style={{
                              width:
                                PDFStyleConfigs.CompositionFollowingCellWidth,
                              borderRight: "1px solid #7C8493",
                              position: "relative",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <View
                              style={{
                                width: "1px",
                                position: "absolute",
                                height: "100%",
                                top: 0,
                                left: "60%",
                                backgroundColor: hexToRGBA("#7C8493", 0.5),
                              }}
                            />

                            <View
                              style={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingTop: 4,
                                paddingBottom: 4,
                              }}
                            >
                              {invalidValues ? (
                                <View
                                  style={{
                                    width: "60%",
                                    alignItems: "flex-end",
                                    justifyContent: "flex-end",
                                    paddingRight: "3%",
                                  }}
                                >
                                  <Text
                                    style={{
                                      ...typographyStyle.body2,
                                      fontSize:
                                        PDFStyleConfigs.CompositionDefaultFontSize,
                                    }}
                                  >
                                    -
                                  </Text>
                                </View>
                              ) : (
                                <View
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    width: "60%",
                                    alignItems: "flex-end",
                                    justifyContent: "flex-end",
                                    paddingRight: "3%",
                                  }}
                                >
                                  {belowMinimumInvestmentSize && (
                                    <Text
                                      style={{
                                        ...typographyStyle.body2,
                                        color: palette.error.main,
                                        fontSize:
                                          PDFStyleConfigs.CompositionDefaultFontSize,
                                      }}
                                    >
                                      *
                                    </Text>
                                  )}
                                  <Text
                                    style={{
                                      ...typographyStyle.body2,
                                      color: palette.text.primary,
                                      fontSize:
                                        PDFStyleConfigs.CompositionDefaultFontSize,
                                    }}
                                  >
                                    ${renderNumberValue(numberValue)}
                                  </Text>
                                </View>
                              )}

                              {invalidValues ? (
                                <View
                                  style={{
                                    width: "40%",
                                    alignItems: "flex-end",
                                    justifyContent: "flex-end",
                                    paddingRight: "3%",
                                  }}
                                >
                                  <Text
                                    style={{
                                      ...typographyStyle.body2,
                                      fontSize:
                                        PDFStyleConfigs.CompositionDefaultFontSize,
                                    }}
                                  >
                                    -
                                  </Text>
                                </View>
                              ) : (
                                <View
                                  style={{
                                    width: "40%",
                                    alignItems: "flex-end",
                                    justifyContent: "flex-end",
                                    display: "flex",
                                    flexDirection: "row",
                                    paddingRight: "3%",
                                  }}
                                >
                                  <Text
                                    style={{
                                      ...typographyStyle.body2,
                                      color: palette.text.primary,
                                      fontSize:
                                        PDFStyleConfigs.CompositionDefaultFontSize,
                                    }}
                                  >
                                    {renderNumberValue(numberValueInPercent)}%
                                  </Text>
                                </View>
                              )}
                            </View>
                          </View>
                        );
                      })}
                    </PDFTableRow>
                  );
                });
              })}
            </PDFTableBody>
          </PDFTable>
          {belowMinimumInvestmentSize && (
            <View style={{ marginTop: 5 }}>
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.error.main,
                }}
              >
                * Below minimum investment size
              </Text>
            </View>
          )}
        </>
      )}

      {!isNil(numOfHiddenProducts) && (
        <View style={{ marginTop: 15 }}>
          <Text
            style={{
              ...typographyStyle.body2,
              color: "#1E1970",
              fontWeight: "bold",
            }}
          >
            {`${numOfHiddenProducts} ${
              numOfHiddenProducts === 1 ? "product" : "products"
            } hidden`}
          </Text>
        </View>
      )}
    </View>
  );
};
